"use client";

import React, { useState, useRef } from "react";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { PageFuturesSchema } from "@/sanity/pages/PageFuturesTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { SanityVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import ChipStatue from "@/components/global/ui/ChipStatue";
import { MediaPlayerInstance } from "@vidstack/react";

import atlas from "@/public/global/atlas.png";
import atlasFutures from "@/public/futures/atlas.png";
import ipad from "@/public/global/ipad.png";
import arrowRow from "@/public/home/arrow-row.svg";
import phemex from "@/public/home/phemex.svg";
import gbe from "@/public/home/gbe.svg";
import play from "@/public/global/svg/play.svg";
import ChipSerif from "@/components/global/ui/ChipSerif";

export default function IntroSection({
  data,
  className,
  futures = false,
}: {
  data: PageHomeSchema | PageFuturesSchema;
  className?: string;
  futures?: boolean;
}) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const playerRef = useRef<MediaPlayerInstance>(null);

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
    if (playerRef.current) {
      playerRef.current.play();
    }
  };

  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block",
            )}
          />
        ))}
      </div>

      <div className="container flex relative flex-col items-center pt-[2rem] pb-[12rem] lg:pt-[3rem] lg:pb-[25rem]">
        <div className="bg-white w-[min(40rem,100%)] sm:w-[min(60rem,100%)] lg:w-[min(90rem,100%)] border border-[#E5E5E5] flex items-center gap-[2rem] lg:gap-[4.5rem] p-[2.25rem] lg:p-[3rem] mb-[5.5rem] lg:mb-[10.23rem]">
          <p className="flex-shrink-0 mb-0 leading-[1.2] text-[1.125rem] text-[#414141] w-[12rem] md:text-[1.5rem] md:w-[16rem]">
            {data?.introPartner}
          </p>
          <Image className="grow" src={arrowRow} alt="arrows" />
          <Image
            className="!w-[10.5rem] lg:!w-[14.5rem] flex-shrink-0"
            src={phemex}
            alt="phemex"
          />
          <Image
            className="!w-[7.5rem] lg:!w-[11.5rem] flex-shrink-0"
            src={gbe}
            alt="gbe"
          />
        </div>

        <div className="bg-white w-[min(40rem,100%)] sm:w-[min(60rem,100%)] lg:w-[min(90rem,100%)] border border-[#E5E5E5] flex flex-col items-center gap-[.75rem] lg:gap-[1.5rem] pt-[3rem] lg:pt-[8.5rem] pb-[5rem] lg:pb-[9rem]">
          <ChipSerif
            className="!text-[#D1A080] mb-0"
            text={
              typeof data?.introChip === 'string'
                ? data.introChip
                : `${(data.introChip as { text1: string; text2: string })?.text1 || ''} ${(data.introChip as { text1: string; text2: string })?.text2 || ''}`.trim()
            }
          />

          {data?.introText && (
            <div className="richtext w-[min(60rem,100%)]">
              <PortableText
                value={data.introText}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        <div className="w-[min(120rem,100%)] relative flex flex-col items-center">
          <div className={cn("hidden md:block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[110%] lg:w-[130.75rem] h-[calc(100%-14rem)] rounded-[1.25rem] lg:rounded-[2.25rem]", futures ? "bg-[#181C3F]" : "bg-[#103037]")} />
          <div className={cn("absolute top-1/2 left-0 -translate-y-1/2 w-full h-[calc(100%-2rem)] lg:h-[calc(100%-4rem)] backdrop-blur-[2rem] rounded-[1.25rem] lg:rounded-[2.25rem]", futures ? "bg-[#2C2E9B]/[.55]" : "bg-[#489268]/[.55]")} />

          <div className="relative !w-[min(106.5rem,92%)] flex items-center justify-center">
            <Image className="absolute !w-full" src={ipad} alt="ipad asset" />

            {data?.introVideo && (
              <div className="w-[calc(100%-3rem)] sm:w-[calc(100%-5rem)] lg:w-[calc(100%-6rem)] aspect-[1192/822] rounded-[1.5rem] overflow-clip relative">
                <style>{`
                  .hide-video-controls .vds-controls {
                    opacity: 0 !important;
                    pointer-events: none !important;
                  }
                `}</style>

                {/* Custom Play Button Overlay - Style this as needed */}
                {!isVideoPlaying && (
                  <button
                    className="flex absolute top-1/2 left-1/2 z-40 items-center rounded-full -translate-x-1/2 -translate-y-1/2 gap-[1rem] h-[7.5rem] px-[3rem] bg-white/10 backdrop-blur-[.5rem] shadow-[0_4px_8px_0_rgba(255,255,255,0.15)_inset,0_-4px_8px_0_rgba(0,0,0,0.15)_inset] gradient-border-white-3 before:opacity-70"
                    onClick={handlePlayVideo}
                    aria-label="Play video"
                  >
                    <p className="mb-0 font-semibold tracking-tight leading-none text-white font-instrument-sans text-[2.25rem]">
                      See video
                    </p>

                    <Image
                      className="!w-[2.75rem]"
                      src={play}
                      alt="play icon"
                    />
                  </button>
                )}

                <SanityVideoPlayer
                  ref={playerRef}
                  playerData={data.introVideo}
                  playsinline={true}
                  className={cn(
                    "!h-full !w-full",
                    !isVideoPlaying && "hide-video-controls",
                  )}
                  onPlay={() => setIsVideoPlaying(true)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
